/* eslint-disable no-undef */
import { BASE_URL_API, MULTIPLIER, RENT, DEFAULT_ENTRY_FIELDS } from '../misc/consts';
import { locale } from './../alpine-functions';

var markers = [];

const DEFAULT_SORT_QUERY = '&sort=-date';

export default () => ({
  map: null,
  infoWindows: [],
  markers: [],
  activeMarker: null,
  realEstates: [],
  defaultIcon: {
    url: '/img/pin.svg', // Default image URL
    scaledSize: new google.maps.Size(34, 34),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(17, 34),
  },
  clickedIcon: {
    url: '/img/pin_active.svg', // Image URL when clicked
    scaledSize: new google.maps.Size(34, 34),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(17, 34),
  },
  filter: null,
  ogFilter: null,
  oldQuery: '',

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.initializeMap();
      this.ogFilter = this.$store.filter.options;
      this.$watch('ogFilter', () => {
        // filter markers when filter changes
        this.filterPins();
      });
    });
  },

  filterPins() {
    const keysToRemove = [
      'gps_latitude:gte',
      'gps_latitude:lte',
      'gps_longitude:gte',
      'gps_longitude:lte',
    ];

    //remove gps from filter to show markers outside of the bounds
    this.filter = Object.fromEntries(
      Object.entries(this.ogFilter).filter(
        ([key]) => !keysToRemove.includes(key)
      )
    );

    // only fetch data if filter has changed
    if (this.oldQuery !== this.filterQuery) {
      this.oldQuery = this.filterQuery;
      this.fetchData();
    }
  },

  initializeMap() {
    const mapOptions = {
      zoom: 12,
      clickableIcons: false,
      disableDefaultUI: true,
      center: new google.maps.LatLng(25.10405685581248, 55.153470399314834),
      styles: [
        {
          featureType: 'administrative',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#444444' }],
        },
        {
          featureType: 'administrative.country',
          elementType: 'geometry.fill',
          stylers: [{ visibility: 'on' }],
        },
        {
          featureType: 'landscape',
          elementType: 'all',
          stylers: [{ color: '#e9e9e9' }],
        },
        {
          featureType: 'poi',
          elementType: 'all',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'road',
          elementType: 'all',
          stylers: [{ saturation: '-100' }, { lightness: 45 }],
        },
        {
          featureType: 'road.highway',
          elementType: 'all',
          stylers: [{ visibility: 'simplified' }],
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry.fill',
          stylers: [{ color: '#ced7db' }],
        },
        {
          featureType: 'road.highway',
          elementType: 'labels.icon',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'road.arterial',
          elementType: 'labels.icon',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'transit',
          elementType: 'all',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'water',
          elementType: 'all',
          stylers: [{ color: '#4694ec' }, { visibility: 'on' }],
        },
      ],
    };

    const mapElement = document.getElementById('map');
    this.map = new google.maps.Map(mapElement, mapOptions);

    // update bounds after the map is resized or moved
    this.map.addListener('idle', this.updateBounds.bind(this));
  },

  closeAllInfoWindows() {
    this.infoWindows.forEach((infoWindow) => {
      infoWindow.close();
    });
  },

  updateBounds() {
    const bounds = this.map.getBounds();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const minLat = sw.lat();
    const minLng = sw.lng();
    const maxLat = ne.lat();
    const maxLng = ne.lng();

    this.$store.filter.set({ filter: 'gps_latitude:gte', values: minLat });
    this.$store.filter.set({ filter: 'gps_latitude:lte', values: maxLat });
    this.$store.filter.set({ filter: 'gps_longitude:gte', values: minLng });
    this.$store.filter.set({ filter: 'gps_longitude:lte', values: maxLng });
  },

  // create filter query for api
  get filterQuery() {
    const { filter } = this;
    if (!filter) {
      return '';
    }
    const filterKeys = Object.keys(filter).filter(
      (key) => Array.isArray(filter[key]) && filter[key].length > 0
    );
    const query = filterKeys
      .map((key) => {
        const separator = key.includes('taxonomy') ? ',' : '|';
        let param = filter[key].map((cur) => cur.value).join(separator);
        if (key === 'usable_area:gte') {
          param = Math.round(param / MULTIPLIER);
        }
        return key === 'sort' ? `sort=${param}` : `filter[${key}]=${param}`;
      })
      .join('&');

    const stringQuery = query ? `${query}` : '';

    return stringQuery.includes('&sort=')
      ? stringQuery
      : stringQuery + DEFAULT_SORT_QUERY;
  },

  fetchData() {
    fetch(
      `/api/collections/real_estates/entries?&limit=9000&filter[site]=${locale}&filter[offer_type]=${RENT}&${this.filterQuery}&fields=${DEFAULT_ENTRY_FIELDS}`
    )
      .then((response) => response.json())
      .then((response) => {
        const realEstatesIds = this.realEstates.map((item) => item.id);
        const responseDataIds = response.data.map((item) => item.id);

        // markers to show
        const toAdd = response.data.filter(
          (item) => !realEstatesIds.includes(item.id)
        );

        //markers to remove from map
        const toRemove = this.realEstates.filter(
          (item) => !responseDataIds.includes(item.id)
        );

        toAdd.forEach((realEstate) => {
          if (markers.some((obj) => obj.slug == realEstate.slug)) {
            // check if marker already exists
            markers
              .find((marker) => marker.slug == realEstate.slug)
              .setMap(this.map);
          } else {
            this.makeMarker(realEstate); // create new marker
          }
        });

        // remove markers from map
        const toRemoveIds = new Set(toRemove.map((item) => item.id));
        for (let i = 0; i < markers.length; i++) {
          if (toRemoveIds.has(markers[i].id)) {
            markers[i].setMap(null);
          }
        }

        this.realEstates = response.data;
      });
  },

  makeMarker(realEstate) {
    // create marker
    const marker = new google.maps.Marker({
      position: new google.maps.LatLng(realEstate.gps.lat, realEstate.gps.lng),
      map: this.map,
      icon: this.defaultIcon,
      id: realEstate.id,
    });

    console.log(realEstate.gps.lat, realEstate.gps.lng);

    // create info window for marker
    const infowindow = new google.maps.InfoWindow({
      content: `<a href="/${locale == 'cs' ? 'cs/pronajem-nemovitosti-v-dubaji' : 'rent-real-estates-in-dubai'}/${realEstate.slug}" class="map-dialog"><img src="${
        realEstate.hero_image
      }" width="247" height="139" loading="lazy"><div class="map-dialog__text"><h4>${
        realEstate.title
      }</h4><p>${this.$store.currency.formatPrice(
        realEstate.property_price,
        true
      )}</p><i class="fa-sharp fa-solid fa-chevron-right"></i></div></a>`,
    });
    markers.push(marker);
    this.infoWindows.push(infowindow);

    marker.addListener('click', () => {
      this.closeAllInfoWindows();
      if (this.activeMarker) {
        this.activeMarker.setIcon(this.defaultIcon);
      }
      infowindow.open(this.map, marker);
      marker.setIcon(this.clickedIcon);
      this.activeMarker = marker;
    });

    infowindow.addListener('closeclick', () => {
      marker.setIcon(this.defaultIcon);
      this.activeMarker = null;
    });
  },

  reset() {
    this.ogFilter = this.$store.filter.options;
  },
});
